import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Delete as DeleteIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axios from '../api/axios';
import { formatDate } from '../utils/format';

interface Member {
  id: string;
  recordType: 'user' | 'invitation';
  role: 'owner' | 'host' | 'guest';
  email: string;
  firstName: string;
  lastName: string;
  status: 'active' | 'pending';
  addedAt: string;
  removable: boolean;
}

interface GuestListProps {
  houseId: string;
  isHost: boolean;
}

export const GuestList: React.FC<GuestListProps> = ({ houseId, isHost }) => {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<Member | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const { t, i18n } = useTranslation();

  const fetchGuests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      const response = await axios.get(`/houses/${houseId}/members`);
      setMembers(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('guests.failedFetch'));
    } finally {
      setLoading(false);
    }
  }, [houseId, t]);

  useEffect(() => {
    fetchGuests();
  }, [fetchGuests]);

  const handleDeleteClick = (member: Member) => {
    setMemberToDelete(member);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!memberToDelete) return;

    try {
      setDeleting(true);
      if (memberToDelete.recordType === 'invitation') {
        await axios.delete(`/houses/${houseId}/invitations/${memberToDelete.id}`);
      } else if (memberToDelete.role === 'host') {
        await axios.delete(`/houses/${houseId}/hosts/${memberToDelete.id}`);
      } else if (memberToDelete.role === 'guest') {
        await axios.delete(`/houses/${houseId}/guests/${memberToDelete.id}`);
      }

      setMembers(members.filter(member => member.id !== memberToDelete.id));
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
    } catch (err: any) {
      setError(err.response?.data?.message || t('guests.failedRemove'));
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setMemberToDelete(null);
  };

  const handleResendInvitation = async (member: Member) => {
    try {
      setResendingId(member.id);
      setError(null);
      setSuccessMessage(null);
      await axios.post(`/invitations/${member.id}/resend`);
      setSuccessMessage(t('guests.resent'));
    } catch (err: any) {
      setError(err.response?.data?.message || t('guests.failedResend'));
    } finally {
      setResendingId(null);
    }
  };

  const getDisplayName = (member: Member) => {
    const fullName = `${member.firstName || ''} ${member.lastName || ''}`.trim();
    return fullName || member.email;
  };

  const getRemovalMessage = () => {
    if (!memberToDelete) {
      return '';
    }

    const translationKey = memberToDelete.status === 'pending'
      ? 'guests.removeInvitationMessage'
      : 'guests.removeMessage';

    return t(translationKey, {
      name: getDisplayName(memberToDelete),
      role: t(`common.roles.${memberToDelete.role}`),
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  if (members.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('guests.empty')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      <List>
        {members.map((member) => (
          <ListItem
            key={`${member.recordType}-${member.id}`}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              mb: 1,
            }}
          >
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1">
                    {getDisplayName(member)}
                  </Typography>
                  <Chip
                    label={t(`common.roles.${member.role}`)}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={t(`common.statuses.${member.status}`)}
                    size="small"
                    color={member.status === 'active' ? 'success' : 'warning'}
                  />
                </Box>
              }
              secondary={
                <>
                  <Typography variant="body2" color="text.secondary">
                    {member.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {member.status === 'pending'
                      ? t('common.labels.invitedOn', { date: formatDate(member.addedAt, i18n.resolvedLanguage) })
                      : t('common.labels.addedOn', { date: formatDate(member.addedAt, i18n.resolvedLanguage) })}
                  </Typography>
                </>
              }
            />
            {isHost && member.removable && (
              <ListItemSecondaryAction>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {member.recordType === 'invitation' && (
                    <IconButton
                      edge="end"
                      aria-label={t('guests.resendInvitationAria')}
                      onClick={() => handleResendInvitation(member)}
                      disabled={resendingId === member.id}
                      color="primary"
                    >
                      <RefreshIcon />
                    </IconButton>
                  )}
                  <IconButton
                    edge="end"
                    aria-label={t('guests.removeGuestAria')}
                    onClick={() => handleDeleteClick(member)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </ListItemSecondaryAction>
            )}
          </ListItem>
        ))}
      </List>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t('guests.removeTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{getRemovalMessage()}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            {t('common.actions.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleting}>
            {deleting ? t('guests.removing') : t('common.actions.remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GuestList;
